import type {
  GuidedAnswer,
  GuidedModeDefinition,
  GuidedModeStep,
} from '../types/guided-mode.types'
import { assessmentQuestionSteps, questionForStep } from './assessment-definition'
import { skippedConceptIds } from './visibility'
import type { GuidedModeAnswers } from './step-engine'

import {UNANSWERED_SENTINEL} from '@shared/clinical-logic/helpers/unansweredSentinel.js'

export { UNANSWERED_SENTINEL }

/*  One entry per question of the track, exactly as `assess()` wants to receive it:

      name   the MRX question name, taken from the concept's code
      value  the answer, or the sentinel when nobody reached the question
      isSkip present only on the questions nobody answered, marking the ones the skip logic
             definitively hid (as opposed to the ones still ahead of the user)

    `assess()` needs all three: without the unanswered questions it cannot tell a question the
    user skipped from one they never got to, and without `isSkip` it would treat every gated
    follow-up the user has not been offered yet as skipped.
*/
export type AssessmentPayloadEntry = {
  type: 'response'
  name: string
  value: string | number
} & ({ isSkip: true } | { isSkip?: undefined })

function flattenQuestionSteps(steps: GuidedModeStep[]): GuidedModeStep[] {
  return steps.flatMap(step => (step.kind === 'group'
    ? flattenQuestionSteps(step.steps || [])
    : [step]))
}

function payloadValue(answer: GuidedAnswer): string | number {
  if (Array.isArray(answer)) return UNANSWERED_SENTINEL
  if (answer === undefined || answer === null || answer === '') return UNANSWERED_SENTINEL
  return answer
}

/*  A question with a real answer is reported as-is. Only a question nobody reached carries
    the skip flag, and only when the skip logic is what kept it off the screen: a gated
    question the user has not come across yet is still ahead of them, not behind them. */
function skipFlagOf(unanswered: boolean, skip: boolean): { isSkip: true } | { isSkip?: undefined } {
  return unanswered && skip ? { isSkip: true } : {}
}

interface BuildAssessmentPayloadOptions {
  /*  Concepts whose question is not part of the clinical payload, e.g. the legacy
      mode-selector questions the guided flow never asks. */
  excludedDataPointCodes?: readonly string[]
  focusedPanelNames?: readonly string[]
  /*  Recorded symptom answers that stay in the bundle while their question is hidden. */
  keptSymptomConceptIds?: readonly string[]
}

export function buildAssessmentPayload(
  definition: GuidedModeDefinition | null | undefined,
  answers: Record<string, GuidedAnswer>,
  {
    excludedDataPointCodes = [],
    focusedPanelNames,
    keptSymptomConceptIds = [],
  }: BuildAssessmentPayloadOptions = {},
): AssessmentPayloadEntry[] {
  if (!definition) return []

  const skipped = skippedConceptIds(definition, answers, keptSymptomConceptIds)

  const steps = assessmentQuestionSteps(definition, excludedDataPointCodes, focusedPanelNames)

  return flattenQuestionSteps(steps)
    .reduce<AssessmentPayloadEntry[]>((entries, step) => {
      const question = questionForStep(definition, step)
      const name = question && definition.concepts?.[question.conceptId]?.code?.code
      if (!question || !name) return entries

      // A question can appear on more than one step (an aggregate multiselect rendered in
      // several groups). One entry per concept is enough, and the first one wins so the flag
      // is stable rather than depending on which duplicate is visited last.
      if (entries.some(entry => entry.name === name)) return entries

      const value = payloadValue(answers[question.conceptId])

      entries.push({
        type: 'response',
        name,
        value,
        ...skipFlagOf(value === UNANSWERED_SENTINEL, skipped.has(question.conceptId)),
      })
      return entries
    }, [])
}

export function buildUnaskedQuestionNames(
  definition: GuidedModeDefinition | null | undefined,
  answers: GuidedModeAnswers,
  options: BuildAssessmentPayloadOptions = {},
): string[] {
  return buildAssessmentPayload(definition, answers, options)
    .filter(entry => entry.value === UNANSWERED_SENTINEL && !entry.isSkip)
    .map(entry => entry.name)
}